/**
 * BillingStateModal — iter-230bw Phase 3 Option-C hook
 * ----------------------------------------------------
 * Pops when the backend returns HTTP-400 `BILLING_STATE_REQUIRED`
 * (e.g. on invoice PDF download or subscription create). Vendor
 * picks their registered GST state + (optionally) types their GSTIN,
 * we POST /api/subscription-billing/billing-state, then signal the
 * caller to retry the action that triggered us.
 */
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { MapPin, Loader2 } from 'lucide-react';

// Subset matching backend GST_STATE_CODES — keep in sync with
// /app/backend/routes/invoice_pdf.py
const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Chandigarh', 'Puducherry',
  'Andaman and Nicobar Islands', 'Dadra and Nagar Haveli and Daman and Diu',
  'Lakshadweep',
];

export default function BillingStateModal({ isOpen, onClose, onSaved, reason }) {
  const { apiCall } = useAuth();
  const [state, setState] = useState('');
  const [gstin, setGstin] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!state) {
      toast.error('Please pick your registered GST state.');
      return;
    }
    setSaving(true);
    try {
      await apiCall('/api/subscription-billing/billing-state', {
        method: 'POST',
        body: JSON.stringify({ state, gstin: gstin || null }),
      });
      toast.success('Billing state saved.');
      onSaved?.({ state, gstin });
      onClose?.();
    } catch (e) {
      toast.error(e?.message || 'Could not save billing state.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="billing-state-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" />
            We need your GST state
          </DialogTitle>
          <DialogDescription>
            {reason ||
              'To generate a GST invoice for this payment, we need to know your registered state. This is a one-time setup.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="billing-state-select">Registered State *</Label>
            <Select value={state} onValueChange={setState}>
              <SelectTrigger id="billing-state-select" data-testid="billing-state-select">
                <SelectValue placeholder="Select your state" />
              </SelectTrigger>
              <SelectContent>
                {INDIAN_STATES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="billing-gstin">GSTIN (optional)</Label>
            <Input
              id="billing-gstin"
              data-testid="billing-state-gstin"
              value={gstin}
              onChange={(e) => setGstin(e.target.value.toUpperCase())}
              placeholder="e.g. 27ABCDE1234F1Z5"
              maxLength={15}
            />
            <p className="text-xs text-slate-500 mt-1">
              Adding your GSTIN unlocks input-tax credit on future invoices.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !state} data-testid="billing-state-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save & continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
